import React, { useState, useEffect } from 'react';
import {
	Box,
	Typography,
	Button,
	TextField,
	Stack,
	Modal,
	IconButton,
	Divider,
	Alert,
	CircularProgress
} from '@mui/material';
import {
	Close,
	Upload,
	CheckCircle,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { applyForJob, resetApplyStatus, selectJobs } from '../../../store/slices/jobsSlice';
import { Job } from '../types';

interface JobApplicationModalProps {
	open: boolean;
	onClose: () => void;
	selectedJob: Job | null;
}

const JobApplicationModal: React.FC<JobApplicationModalProps> = ({
	open,
	onClose,
	selectedJob,
}) => {
	const [formData, setFormData] = useState({
		name: '',
		phone: '',
		email: '',
		designation: '',
		cvFile: null as File | null
	});
	const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
	const [resumeBase64, setResumeBase64] = useState<string>('');

	const dispatch = useAppDispatch();
	const { applyLoading, applyError } = useAppSelector(selectJobs);

	useEffect(() => {
		if (open) {
			dispatch(resetApplyStatus());
			// Auto-fill designation with job title
			if (selectedJob) {
				setFormData(prev => ({
					...prev,
					designation: selectedJob.title
				}));
			}
		}
	}, [open, dispatch, selectedJob]);

	useEffect(() => {
		if (applyLoading === 'succeeded') {
			// Reset form and close modal after successful submission
			setTimeout(() => {
				handleClose();
			}, 2000);
		}
	}, [applyLoading]);

	const handleInputChange = (field: string, value: string) => {
		// For phone field, restrict to 11 digits
		if (field === 'phone' && value.length > 11) {
			return;
		}
		
		setFormData(prev => ({ ...prev, [field]: value }));
		if (formErrors[field]) {
			setFormErrors(prev => ({ ...prev, [field]: '' }));
		}
	};

	const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (file) {
			if (file.type === 'application/pdf') {
				setFormData(prev => ({ ...prev, cvFile: file }));
				if (formErrors.cvFile) {
					setFormErrors(prev => ({ ...prev, cvFile: '' }));
				}
				
				// Convert file to base64
				const reader = new FileReader();
				reader.onload = (e) => {
					const result = e.target?.result as string;
					// Remove the data:application/pdf;base64, prefix
					const base64 = result.split(',')[1];
					setResumeBase64(base64);
				};
				reader.readAsDataURL(file);
			} else {
				setFormErrors(prev => ({ ...prev, cvFile: 'Please select a PDF file' }));
			}
		}
	};

	const validateForm = () => {
		const errors: {[key: string]: string} = {};

		if (!formData.name.trim()) {
			errors.name = 'Name is required';
		}

		if (!formData.phone.trim()) {
			errors.phone = 'Phone number is required';
		} else if (formData.phone.replace(/\s/g, '').length !== 11) {
			errors.phone = 'Phone number must be 11 digits';
		}

		if (!formData.email.trim()) {
			errors.email = 'Email is required';
		} else if (!/\S+@\S+\.\S+/.test(formData.email)) {
			errors.email = 'Please enter a valid email address';
		}

		if (!formData.designation.trim()) {
			errors.designation = 'Designation is required';
		}

		if (!formData.cvFile) {
			errors.cvFile = 'CV file is required';
		}

		setFormErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		
		if (!validateForm() || !selectedJob) {
			return;
		}

		const application = {
			job_posting_id: selectedJob.id,
			name: formData.name,
			email: formData.email,
			phone: formData.phone,
			designation: formData.designation,
			resume: resumeBase64
		};

		dispatch(applyForJob(application));
	};

	const handleClose = () => {
		onClose();
		// Reset form when closing
		setFormData({
			name: '',
			phone: '',
			email: '',
			designation: '',
			cvFile: null
		});
		setFormErrors({});
		setResumeBase64('');
		dispatch(resetApplyStatus());
	};

	return (
		<Modal
			open={open}
			onClose={handleClose}
			aria-labelledby="apply-modal-title"
			aria-describedby="apply-modal-description"
		>
			<Box sx={{
				position: 'absolute',
				top: '50%',
				left: '50%',
				transform: 'translate(-50%, -50%)',
				width: { xs: '95%', sm: 600 },
				maxHeight: '90vh',
				overflow: 'auto',
				bgcolor: '#18191b',
				borderRadius: 3,
				boxShadow: 24,
				p: 4,
				border: '1px solid #333'
			}}>
				{/* Modal Header */}
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
					<Typography variant="h5" sx={{ fontWeight: 700, color: 'white' }}>
						Apply for {selectedJob?.title}
					</Typography>
					<IconButton onClick={handleClose} sx={{ color: 'grey.400' }}>
						<Close />
					</IconButton>
				</Box>
                
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.23)', mb: 3 }} />

				{/* Error Alert */}
				{applyError && (
					<Alert severity="error" sx={{ mb: 3 }}>
						{applyError}
					</Alert>
				)}

				{applyLoading === 'succeeded' ? (
					<Box sx={{ textAlign: 'center', py: 4 }}>
						<CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
						<Typography variant="h6" sx={{ color: 'success.main', mb: 1 }}>
							Application Submitted Successfully!
						</Typography>
						<Typography variant="body2" sx={{ color: 'grey.400' }}>
							We'll review your application and get back to you soon.
						</Typography>
					</Box>
				) : (
					<form onSubmit={handleSubmit}>
						<Stack spacing={3}>
							{/* Name Field */}
							<TextField
								fullWidth
								label="Full Name"
								value={formData.name}
								onChange={(e) => handleInputChange('name', e.target.value)}
								error={!!formErrors.name}
								helperText={formErrors.name}
								sx={{
									'& .MuiOutlinedInput-root': {
										color: 'white',
										'& fieldset': {
											borderColor: 'rgba(255,255,255,0.23)',
										},
										'&:hover fieldset': {
											borderColor: 'rgba(255,255,255,0.4)',
										},
										'&.Mui-focused fieldset': {
											borderColor: 'secondary.main',
										},
									},
									'& .MuiInputLabel-root': {
										color: 'grey.400',
										'&.Mui-focused': {
											color: 'secondary.main',
										},
									},
									'& .MuiFormHelperText-root': {
										color: 'error.main',
									},
								}}
							/>

							{/* Phone Field */}
							<TextField
								fullWidth
								label="Phone Number"
								value={formData.phone}
								onChange={(e) => handleInputChange('phone', e.target.value)}
								error={!!formErrors.phone}
								helperText={formErrors.phone || `${formData.phone.length}/11 characters`}
								inputProps={{ maxLength: 11 }}
								sx={{
									'& .MuiOutlinedInput-root': {
										color: 'white',
										'& fieldset': {
											borderColor: 'rgba(255,255,255,0.23)',
										},
										'&:hover fieldset': {
											borderColor: 'rgba(255,255,255,0.4)',
										},
										'&.Mui-focused fieldset': {
											borderColor: 'secondary.main',
										},
									},
									'& .MuiInputLabel-root': {
										color: 'grey.400',
										'&.Mui-focused': {
											color: 'secondary.main',
										},
									},
									'& .MuiFormHelperText-root': {
										color: formErrors.phone ? 'error.main' : 'grey.500',
									},
								}}
							/>

							{/* Email Field */}
							<TextField
								fullWidth
								label="Email Address"
								type="email"
								value={formData.email}
								onChange={(e) => handleInputChange('email', e.target.value)}
								error={!!formErrors.email}
								helperText={formErrors.email}
								sx={{
									'& .MuiOutlinedInput-root': {
										color: 'white',
										'& fieldset': {
											borderColor: 'rgba(255,255,255,0.23)',
										},
										'&:hover fieldset': {
											borderColor: 'rgba(255,255,255,0.4)',
										},
										'&.Mui-focused fieldset': {
											borderColor: 'secondary.main',
										},
									},
									'& .MuiInputLabel-root': {
										color: 'grey.400',
										'&.Mui-focused': {
											color: 'secondary.main',
										},
									},
									'& .MuiFormHelperText-root': {
										color: 'error.main',
									},
								}}
							/>

							{/* Designation Field */}
							<TextField
								fullWidth
								label="Designation"
								value={formData.designation}
								onChange={(e) => handleInputChange('designation', e.target.value)}
								error={!!formErrors.designation}
								helperText={formErrors.designation}
								sx={{
									'& .MuiOutlinedInput-root': {
										color: 'white',
										'& fieldset': {
											borderColor: 'rgba(255,255,255,0.23)',
										},
										'&:hover fieldset': {
											borderColor: 'rgba(255,255,255,0.4)',
										},
										'&.Mui-focused fieldset': {
											borderColor: 'secondary.main',
										},
									},
									'& .MuiInputLabel-root': {
										color: 'grey.400',
										'&.Mui-focused': {
											color: 'secondary.main',
										},
									},
									'& .MuiFormHelperText-root': {
										color: 'error.main',
									},
								}}
							/>

							{/* CV Upload */}
							<Box>
								<Typography variant="body2" sx={{ color: 'grey.400', mb: 1 }}>
									Upload CV (PDF only)
								</Typography>
								<Button
									variant="outlined"
									component="label"
									startIcon={<Upload />}
									sx={{
										borderColor: formErrors.cvFile ? 'error.main' : 'rgba(255,255,255,0.23)',
										color: 'white',
										'&:hover': {
											borderColor: 'secondary.main',
											color: 'secondary.main',
										},
									}}
								>
									{formData.cvFile ? formData.cvFile.name : 'Choose File'}
									<input
										type="file"
										hidden
										accept=".pdf"
										onChange={handleFileChange}
									/>
								</Button>
								{formErrors.cvFile && (
									<Typography variant="caption" sx={{ color: 'error.main', display: 'block', mt: 1 }}>
										{formErrors.cvFile}
									</Typography>
								)}
							</Box>

							{/* Submit Button */}
							<Button
								type="submit"
								variant="contained"
								size="large"
								disabled={applyLoading === 'pending'}
								sx={{
									bgcolor: 'secondary.main',
									color: 'white',
									py: 1.5,
									fontSize: 16,
									fontWeight: 600,
									'&:hover': {
										bgcolor: 'secondary.dark',
									},
									'&:disabled': {
										bgcolor: 'grey.600',
									},
								}}
							>
								{applyLoading === 'pending' ? (
									<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
										<CircularProgress size={20} sx={{ color: 'white' }} />
										Submitting...
									</Box>
								) : (
									'Submit Application'
								)}
							</Button>
						</Stack>
					</form>
				)}
			</Box>
		</Modal>
	);
};

export default JobApplicationModal; 